// DEPRECATION NOTICE
//
// PopoverTrigger is deprecated in favor of using either:
// - low level PopoverController
// - higher level Popover, Tooltip and others

import * as React from 'react'
import type {MouseEvent} from 'react'

import {autobind} from '@base/decorators'
import {
  cloneCreateOrCall,
  cloneCreateOrCallNode,
  pickProps,
  omitProps,
} from '@base/react-utils'

import type {AnimationType} from '@base-uikit/animations/CSSAnimation'
import {PopoverBackdrop} from '@base-uikit/layers/backdrops'
import * as variables from '@base-uikit/styles/theme/variables'
import {pxValue} from '@base-uikit/styles/units'
import TetherPosition, {
  TETHER_POSITION_PROPS,
} from '@base-uikit/tether/TetherPosition'
import type {
  CalculateResult,
  PositionOptions,
  AnchorOptions,
  Placements,
  ElementOrRectangleOrFn,
} from '@base-uikit/tether/types'

import PopoverBalloon from './PopoverBalloon'
import PopoverController from './PopoverController'
import type {CloseOutOfViewEnum} from './PopoverController'

export const POPOVER_TRIGGER_PROPS = [
  ...TETHER_POSITION_PROPS,
  'target',
  'container',
  'targetContainer',
  'onPosition',
  'animation',
  'portal',
  'backdrop',
  'focusBoundary',
  'closeOutOfView',
  'closeOnEscape',
  'closeOnOutsideClick',
  'onDidClose',
]

export type PopoverTriggerProps = {
  placement: Placements
  opened: boolean
  popover?:
    | React.ReactElement<any>
    | React.ComponentType<any>
    | (() => React.ReactElement<any>)
  content?:
    | React.ReactNode
    | undefined
    | null
    | React.ComponentType<any>
    | (() => React.ReactElement<any>)
  target?: ElementOrRectangleOrFn | null
  // Container to measure containerRect & visibleRect from
  container?: ElementOrRectangleOrFn | 'auto'
  // Target container to measure targetVisibleRect from
  targetContainer?: ElementOrRectangleOrFn | 'auto'
  containerPadding?:
    | number
    | {
        left?: number
        top?: number
        right?: number
        bottom?: number
      }
  portal?:
    | null
    | React.ComponentType<any>
    | (() => React.ReactElement<any>)
    | undefined
    | null
  animation?: AnimationType | null
  backdrop?:
    | null
    | boolean
    | React.ComponentType<any>
    | ((props: any) => React.ReactElement<any>)
  focusBoundary?: boolean | 'target' | 'popover'
  toggleOnHover?: boolean | number
  toggleOnFocus?: boolean
  toggleOnClick?: boolean
  closeOnOutsideClick?: boolean
  // Closes popover if both popover and/or target goes completely out-of-view
  closeOutOfView?: true | CloseOutOfViewEnum
  closeOnEscape?: boolean
  // TODO children is required, but flow does not handle this :|
  children?: React.ReactElement<any>
  className?: string
  style?: any
  onToggle: (opened: boolean) => void
  // Triggered when closing animation is finished
  onDidClose?: (() => void) | null
  onPosition?: ((a: CalculateResult) => void) | null
  onOutsideClick?: ((a: MouseEvent) => void) | null
} & PositionOptions &
  AnchorOptions

// @deprecated PopoverTrigger is DEPRECATED, use lower level PopoverController
//   or higher level Popover
export default class PopoverTrigger extends React.Component<PopoverTriggerProps> {
  static displayName = 'PopoverTrigger'

  static defaultProps = {
    animation: 'pop',
    container: window,
    placement: ['bottom', 'right', 'left', 'top'],
    containerPadding: pxValue(variables.container.padding),
    anchorPadding: 10,
    popover: PopoverBalloon,
    closeOnEscape: true,
  }

  // old ref API

  get opened(): boolean {
    return this.props.opened
  }

  set opened(opened: boolean) {
    if (this.opened === opened) return
    if (this.props.onToggle) this.props.onToggle(opened)
  }

  @autobind
  open() {
    this.opened = true
  }

  @autobind
  close() {
    this.opened = false
  }

  // ------

  @autobind
  renderPopover(props: any) {
    const {popover, content} = this.props
    const children = cloneCreateOrCallNode(content)
    // @ts-ignore Ignored during migration
    return cloneCreateOrCall(popover, children ? {...props, children} : props)
  }

  @autobind
  renderPosition(positionProps: any) {
    const {container, targetContainer} = this.props
    return (
      <TetherPosition
        {...positionProps}
        {...pickProps(this.props, TETHER_POSITION_PROPS)}
        // whenever "auto" is used, use container from PopoverController
        container={container === 'auto' ? positionProps.container : container}
        targetContainer={
          targetContainer === 'auto'
            ? positionProps.targetContainer
            : targetContainer
        }
      />
    )
  }

  render() {
    const props = omitProps(this.props, [
      TETHER_POSITION_PROPS,
      'popover',
      'content',
      'focusBoundary',
    ])
    const {children, backdrop, focusBoundary, container, targetContainer} =
      this.props

    return React.createElement(
      PopoverController,
      {
        ...props,
        renderPopover: this.renderPopover,
        renderPosition: this.renderPosition,
        wrapper: 'span',
        backdrop: backdrop === true ? PopoverBackdrop : backdrop || null,
        focusBoundaryAt:
          focusBoundary === true
            ? backdrop
              ? 'popover'
              : 'target'
            : focusBoundary,
        limitPopoverToScrollParent: container === 'auto',
        limitTargetToScrollParent: targetContainer === 'auto',
      },
      children
    )
  }
}
